import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile CAPTCHA Widget
 * 
 * Reads site key from REACT_APP_TURNSTILE_SITE_KEY env var (baked at Docker build time).
 * Falls back to hostname detection, then Cloudflare's always-pass test key for dev.
 *
 * IMPORTANT: All callback props (onToken, onExpired, onError) are stored in refs
 * so that parent re-renders do NOT cause the Turnstile iframe to be destroyed
 * and recreated — which would kill the green-checkmark success animation.
 */

const TEST_SITE_KEY = "1x00000000000000000000AA"; // Cloudflare test key — always passes

function getSiteKey() {
  // 1. Environment variable (set via Docker build arg — the correct production method)
  const envKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  if (envKey && envKey.length > 10) return envKey;

  // 2. Hostname fallback (dev convenience)
  if (typeof window !== "undefined" && window.location.hostname === "pulse.spillconsult.com") {
    return "0x4AAAAAADScVkf8IyaPlY1P";
  }

  // 3. Test key for local dev
  return TEST_SITE_KEY;
}

export function TurnstileWidget({ onToken, onExpired, onError, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [widgetError, setWidgetError] = useState("");
  const verifiedRef = useRef(false);
  const timeoutRef = useRef(null);

  // ── Store callbacks in refs so the useEffect never re-fires due to
  //    inline arrow functions in the parent component ──
  const onTokenRef = useRef(onToken);
  const onExpiredRef = useRef(onExpired);
  const onErrorRef = useRef(onError);

  // Keep refs in sync with latest props (no re-render, no effect re-run)
  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);
  useEffect(() => { onExpiredRef.current = onExpired; }, [onExpired]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const resetWidget = () => {
    verifiedRef.current = false;
    setWidgetError("");
    if (widgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch {}
    }
  };

  useEffect(() => {
    const renderWidget = () => {
      if (!containerRef.current) return;
      if (!window.turnstile) {
        setTimeout(renderWidget, 300);
        return;
      }

      // Clean up previous widget
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }

      verifiedRef.current = false;

      const id = window.turnstile.render(containerRef.current, {
        sitekey: getSiteKey(),
        callback: (token) => {
          verifiedRef.current = true;
          setWidgetError("");
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          // Delay delivering the token to the parent by 350ms so that
          // the Turnstile iframe has time to play the green-checkmark
          // animation before any parent state update / re-render occurs.
          setTimeout(() => {
            if (onTokenRef.current) onTokenRef.current(token);
          }, 350);
        },
        "expired-callback": () => {
          verifiedRef.current = false;
          if (onExpiredRef.current) onExpiredRef.current();
        },
        "error-callback": (errCode) => {
          console.error("Turnstile error:", errCode);
          setWidgetError("Verification failed (" + (errCode || "unknown") + "). Click retry.");
          if (onErrorRef.current) onErrorRef.current(errCode);
        },
        theme: "auto",
        size: "normal",
        appearance: "always",
        retry: "auto",
        "retry-interval": 5000,
      });
      widgetIdRef.current = id;

      // Safety timeout: if widget doesn't verify within 20s, show retry option
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (!verifiedRef.current) {
          setWidgetError("Verification is taking too long. Click retry or check your network.");
        }
      }, 20000);
    };

    renderWidget();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
    // ONLY re-run when resetKey changes (explicit reset from parent).
    // Callback props are accessed via refs — never trigger re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return (
    <div data-testid="turnstile-widget" className="my-2">
      <div
        ref={containerRef}
        className="flex justify-center [&_iframe]:!min-h-[65px] [&_iframe]:!min-w-[300px]"
        style={{ minHeight: "65px", overflow: "visible" }}
      />
      {widgetError && (
        <div className="mt-2 text-center space-y-1">
          <p className="text-xs text-destructive">{widgetError}</p>
          <button
            type="button"
            onClick={resetWidget}
            className="text-xs text-primary hover:underline font-medium"
          >
            Retry CAPTCHA
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Helper: add the X-Turnstile-Token header to axios requests.
 */
export function withTurnstile(token) {
  return {
    headers: { "X-Turnstile-Token": token || "" },
  };
}

/**
 * Helper: merge turnstile header into an existing axios config.
 */
export function mergeTurnstile(config = {}, token) {
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      "X-Turnstile-Token": token || "",
    },
  };
}
